import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { checkupTabletAPI, checkupUebungAPI } from '@/lib/api';
import CheckupStepper, { Knopf, fehlerText, tabletClient, uebungClient } from '@/components/CheckupStepper';

/* Geführter Check-Up am Studio-Tablet (28.10.2026), Reiter „Check-Ups“ im Geräte-Link /trainer/<token> (nur mit Haken
   `checkup_tablet`). „Wer bist du?“ → nur die eigenen, angenommenen Termine → Ablauf, ein Schritt je Bildschirm. Der Stand
   kommt immer vom Server (`schritt`); der gewählte Trainer reist bei jedem Aufruf mit, der Server prüft ihn. Nach 10 Minuten
   ohne Eingabe und am Ende eines Check-Ups geht es zurück zur Namenswahl. Keine Browser-Dialoge (confirm/alert).
   Kundenakte Schritt 3+4 (28.10.2026): Der Ablauf selbst ist `CheckupStepper` (gemeinsam mit dem CRM-Panel); die früheren
   Schritte „Personal Training“ und „Ernährung“ sind durch Befund → Empfehlung → Abschluss ersetzt (ihre Daten bleiben lesbar,
   Karte „Check-Up-Ergebnis“). Übungsmodus: Reiter neben „Meine Check-Ups“, derselbe Stepper, nichts zählt. */

const ROT = '#E60000';
const IDLE_MS = 10 * 60 * 1000;
const WOCHENTAGE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

/* heute / morgen / Wochentag + Datum */
function datumText(iso) {
  const d = new Date(`${iso}T12:00:00`);
  const h = new Date(); h.setHours(12, 0, 0, 0);
  const diff = Math.round((d - h) / 86400000);
  const kurz = `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
  return diff === 0 ? `Heute, ${kurz}` : diff === 1 ? `Morgen, ${kurz}` : `${WOCHENTAGE[d.getDay()]}, ${kurz}`;
}

/* Übungsmodus (28.10.2026): derselbe Stepper mit dem Übungs-Client — alles nur im Übungsdokument (`checkup_uebung.py`) */
function Ablauf({ token, trainer, id, onZurueck, uebung = false }) {
  const client = useMemo(() => (uebung ? uebungClient : tabletClient)(token, trainer.id, id), [token, trainer.id, id, uebung]);
  return <CheckupStepper client={client} trainerName={trainer.name} onZurueck={onZurueck} />;
}

/* Übungsmodus (28.10.2026): breites Band, damit niemand eine Übung mit einem echten Check-Up verwechselt */
function UebungBand() {
  return (
    <div className="sticky top-0 z-40 -mx-4 px-4 py-3 text-center text-lg font-extrabold tracking-wide text-black" style={{ background: '#FACC15' }}
      role="status" data-testid="uebung-band">ÜBUNG – zählt nirgends, wird nach 24 h gelöscht</div>
  );
}

function UebungListe({ token, trainer, onOeffnen }) {
  const [liste, setListe] = useState(null);
  const [fehler, setFehler] = useState('');
  const [frage, setFrage] = useState(false);
  const [arbeitet, setArbeitet] = useState(false);
  const laden = useCallback(async () => {
    try { setListe((await checkupUebungAPI.liste(token, trainer.id)).data.uebungen || []); setFehler(''); } catch (e) { setListe([]); setFehler(fehlerText(e)); }
  }, [token, trainer.id]);
  useEffect(() => { laden(); }, [laden]);
  const zuruecksetzen = async () => {
    setArbeitet(true); setFehler('');
    try { setListe((await checkupUebungAPI.zuruecksetzen(token, trainer.id)).data.uebungen || []); setFrage(false); }
    catch (e) { setFehler(fehlerText(e)); }
    finally { setArbeitet(false); }
  };
  return (
    <section className="space-y-4" data-testid="uebung-liste">
      <p className="text-base text-muted-foreground">Fünf erfundene Übungskunden. Derselbe Ablauf wie echt – nichts landet beim Mitglied, im Kalender, im Kontingent oder in einer Statistik. Der Übungsstand wird nach 24 h gelöscht.</p>
      {fehler && <p className="text-red-600">{fehler}</p>}
      {liste === null ? <Loader2 className="w-8 h-8 animate-spin" /> : (
        <ul className="space-y-3">
          {liste.map((u) => (
            <li key={u.id}>
              <button type="button" onClick={() => onOeffnen(u.id)} data-testid={`uebung-${u.id}`}
                className="w-full min-h-[72px] text-left rounded-2xl border border-border p-4 flex items-center justify-between gap-3 active:opacity-80">
                <span className="text-lg"><b>{u.vorname}, {u.alter}</b> · {u.tarif}<span className="block text-sm text-muted-foreground">{u.ausgangslage}</span></span>
                <span className="text-sm text-muted-foreground whitespace-nowrap">{u.status === 'erledigt' ? 'Fertig' : u.schritt ? 'Fortsetzen' : 'Starten'}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <Knopf disabled={arbeitet} onClick={() => setFrage(true)} data-testid="uebung-zuruecksetzen">Übungen zurücksetzen</Knopf>
      {frage && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="zuruecksetzen-rueckfrage">
          <div className="bg-background rounded-2xl p-6 max-w-md w-full space-y-4">
            <p className="text-xl font-bold">Alle Übungen zurücksetzen?</p>
            <p className="text-base text-muted-foreground">Antworten, Messwerte und Vermerke deiner fünf Übungsfälle werden gelöscht. Echte Check-Ups bleiben unberührt.</p>
            <div className="flex gap-3">
              <Knopf rot disabled={arbeitet} onClick={zuruecksetzen}>Ja, zurücksetzen</Knopf>
              <Knopf onClick={() => setFrage(false)}>Abbrechen</Knopf>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default function TabletCheckup({ token }) {
  const [trainerListe, setTrainerListe] = useState(null);
  const [trainer, setTrainer] = useState(null);       // {id, name}
  const [liste, setListe] = useState(null);
  const [offen, setOffen] = useState(null);           // checkup_id
  const [uebung, setUebung] = useState(false);        // Übungsmodus (eigene Routen, nichts zählt)
  const [fehler, setFehler] = useState('');
  const timer = useRef(null);

  const zurueckZurWahl = useCallback(() => { setTrainer(null); setListe(null); setOffen(null); setUebung(false); setFehler(''); }, []);
  // 10 Minuten ohne Eingabe: zurück zur Namenswahl, damit der Nächste nicht die Liste des Vorigen sieht
  useEffect(() => {
    if (!trainer) return undefined;
    const neu = () => { clearTimeout(timer.current); timer.current = setTimeout(zurueckZurWahl, IDLE_MS); };
    neu();
    const ev = ['pointerdown', 'keydown', 'touchstart'];
    ev.forEach((e) => window.addEventListener(e, neu));
    return () => { clearTimeout(timer.current); ev.forEach((e) => window.removeEventListener(e, neu)); };
  }, [trainer, zurueckZurWahl]);

  useEffect(() => {
    if (trainer) return;
    checkupTabletAPI.trainer(token).then((r) => setTrainerListe(r.data.trainer || [])).catch((e) => { setTrainerListe([]); setFehler(fehlerText(e)); });
  }, [token, trainer]);

  const listeLaden = useCallback(async () => {
    if (!trainer) return;
    try { setListe((await checkupTabletAPI.liste(token, trainer.id)).data.checkups || []); setFehler(''); } catch (e) { setListe([]); setFehler(fehlerText(e)); }
  }, [token, trainer]);
  useEffect(() => { if (trainer && !offen && !uebung) listeLaden(); }, [trainer, offen, uebung, listeLaden]);

  const modus = (an) => { setOffen(null); setUebung(an); };

  return (
    <div className="max-w-3xl mx-auto px-4 py-5 space-y-5" data-testid="tablet-checkup">
      {trainer && uebung && <UebungBand />}
      {!trainer ? (
        <section className="space-y-4">
          <h2 className="text-2xl font-bold">Wer bist du?</h2>
          {fehler && <p className="text-red-600">{fehler}</p>}
          {trainerListe === null ? <Loader2 className="w-8 h-8 animate-spin" /> : trainerListe.length === 0 ? <p className="text-muted-foreground">Zurzeit ist niemand für Check-Ups eingetragen.</p> : (
            <div className="grid grid-cols-2 gap-3">
              {trainerListe.map((t) => <Knopf key={t.id} className="min-h-[72px] text-xl" onClick={() => setTrainer(t)} data-testid={`wahl-${t.id}`}>{t.name}</Knopf>)}
            </div>
          )}
        </section>
      ) : offen ? (
        <Ablauf key={`${uebung ? 'u' : 'e'}:${offen}`} token={token} trainer={trainer} id={offen} onZurueck={() => setOffen(null)}
          uebung={uebung} />
      ) : (
        <section className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div><p className="text-sm text-muted-foreground">Durchgeführt von</p><h2 className="text-2xl font-bold" data-testid="durchgefuehrt-von">{trainer.name}</h2></div>
            <Knopf onClick={zurueckZurWahl}>Trainer wechseln</Knopf>
          </div>
          <div className="grid grid-cols-2 gap-2 rounded-2xl border border-border p-1" role="tablist" data-testid="modus-wahl">
            {[[false, 'Meine Check-Ups'], [true, 'Übungsmodus']].map(([an, l]) => (
              <button key={l} type="button" role="tab" aria-selected={uebung === an} onClick={() => modus(an)} data-testid={an ? 'modus-uebung' : 'modus-echt'}
                className={`min-h-[52px] rounded-xl text-base font-semibold ${uebung === an ? 'text-white' : ''}`}
                style={uebung === an ? { background: an ? '#B45309' : ROT } : undefined}>{l}</button>
            ))}
          </div>
          {uebung ? <UebungListe token={token} trainer={trainer} onOeffnen={setOffen} /> : (
            <>
              {fehler && <p className="text-red-600">{fehler}</p>}
              {liste === null ? <Loader2 className="w-8 h-8 animate-spin" /> : liste.length === 0 ? (
                <p className="text-muted-foreground text-base">Keine Check-Up-Termine in den nächsten 14 Tagen. Neue Termine übernimmst du in der Trainer-App.</p>
              ) : (
                <ul className="space-y-3">
                  {liste.map((e) => (
                    <li key={e.id}>
                      <button type="button" onClick={() => setOffen(e.id)} data-testid={`checkup-${e.id}`}
                        className="w-full min-h-[72px] text-left rounded-2xl border border-border p-4 flex items-center justify-between gap-3 active:opacity-80">
                        <span className="text-lg"><b>{datumText(e.datum)}, {e.start || '–'} Uhr</b> · {e.vorname}</span>
                        <span className="text-sm text-muted-foreground">{e.schritt ? 'Fortsetzen' : 'Starten'}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
